// Facebook and Instagram Integration Implementation
// Provides integration with Facebook Graph API for both Facebook Pages and Instagram Business accounts

import { BaseSocialIntegration, SocialPlatform, SocialPost, SocialEngagement, SocialAudience } from '../base/BaseSocialIntegration';
import { IntegrationConfig, ConnectionResult, TestResult, ValidationResult } from '../../../types/integration';

interface FacebookCredentials {
  accessToken: string;
  appId: string;
  appSecret: string;
  pageId?: string;
  instagramBusinessAccountId?: string;
}

interface FacebookPageInfo {
  id: string;
  name: string;
  category: string;
  accessToken: string;
  instagramBusinessAccount?: {
    id: string;
    username: string;
  };
}

interface _FacebookPostResponse {
  id: string;
  created_time: string;
  message?: string;
  story?: string;
  permalink_url: string;
  insights?: {
    data: Array<{
      name: string;
      values: Array<{
        value: number;
      }>;
    }>;
  };
}

interface _InstagramMediaResponse {
  id: string;
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  media_url: string;
  permalink: string;
  caption?: string;
  timestamp: string;
  insights?: {
    data: Array<{
      name: string;
      values: Array<{
        value: number;
      }>;
    }>;
  };
}

interface _FacebookAudienceResponse {
  id: string;
  name: string;
  approximate_count: number;
  delivery_status: {
    code: number;
    description: string;
  };
}

export class FacebookInstagramIntegration extends BaseSocialIntegration {
  private credentials: FacebookCredentials;
  private baseUrl = 'https://graph.facebook.com/v18.0';
  private connectedPages: FacebookPageInfo[] = [];
  private isConnected = false;
  private lastConnectedAt?: Date;
  private lastSyncAt?: Date;

  constructor(config: IntegrationConfig) {
    super(config);
    // Extract credentials from encrypted format - in real implementation would decrypt
    this.credentials = (config.credentials as unknown as FacebookCredentials);
  }

  async connect(): Promise<ConnectionResult> {
    try {
      // Verify access token and get user pages
      const response = await this.makeRequest('/me/accounts', {
        fields: 'id,name,category,access_token,instagram_business_account{id,username}'
      });

      this.connectedPages = response.data;
      
      // Update connection status
      this.isConnected = true;
      this.lastConnectedAt = new Date();

      // Connection successful - would emit event in real implementation
      console.log('Facebook/Instagram connected:', {
        pagesCount: this.connectedPages.length,
        hasInstagram: this.connectedPages.some(page => page.instagramBusinessAccount)
      });

      return {
        success: true,
        message: `Connected to ${this.connectedPages.length} Facebook pages`,
        connectionId: `facebook_instagram_${Date.now()}`,
        capabilities: ['post_publishing', 'audience_sync', 'engagement_tracking'],
        metadata: {
          pagesCount: this.connectedPages.length,
          hasInstagram: this.connectedPages.some(page => page.instagramBusinessAccount)
        }
      };

    } catch (error) {
      console.error('Facebook/Instagram connection failed:', error instanceof Error ? error.message : 'Unknown error');
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async disconnect(): Promise<void> {
    try {
      this.isConnected = false;
      this.connectedPages = [];
      
      console.log('Facebook/Instagram disconnected');
    } catch (error) {
      console.error('Facebook/Instagram disconnection failed:', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    try {
      const response = await this.makeRequest('/me', { fields: 'id,name' });
      
      if (response.id) {
        return {
          success: true,
          message: `Connected as ${response.name}`,
          duration: Date.now() - startTime,
          details: { userId: response.id, userName: response.name }
        };
      }
      
      return {
        success: false,
        message: 'Invalid response from Facebook API',
        duration: Date.now() - startTime
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Connection test failed',
        duration: Date.now() - startTime
      };
    }
  }

  async validateConfig(): Promise<ValidationResult> {
    const errors: Array<{ field: string; message: string; code: string }> = [];
    const warnings: Array<{ field: string; message: string; suggestion?: string }> = [];
    
    if (!this.credentials.accessToken) {
      errors.push({ field: 'accessToken', message: 'Access token is required', code: 'REQUIRED' });
    }
    
    if (!this.credentials.appId) {
      errors.push({ field: 'appId', message: 'App ID is required', code: 'REQUIRED' });
    }
    
    if (!this.credentials.appSecret) {
      errors.push({ field: 'appSecret', message: 'App secret is required', code: 'REQUIRED' });
    }
    
    // Add warnings for optional fields
    if (!this.credentials.pageId) {
      warnings.push({ 
        field: 'pageId', 
        message: 'Page ID not specified - will auto-detect from access token',
        suggestion: 'Specify a page ID for better performance'
      });
    }
    
    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  async getConnectedPlatforms(): Promise<SocialPlatform[]> {
    const platforms: SocialPlatform[] = [];

    for (const page of this.connectedPages) {
      // Facebook Page
      platforms.push({
        id: page.id,
        name: 'facebook',
        displayName: `Facebook - ${page.name}`,
        connected: true,
        accountId: page.id,
        accountName: page.name,
        permissions: ['pages_manage_posts', 'pages_read_engagement'],
        connectedAt: this.lastConnectedAt || new Date(),
        lastSyncAt: this.lastSyncAt
      });

      // Instagram Business Account (if connected)
      if (page.instagramBusinessAccount) {
        platforms.push({
          id: page.instagramBusinessAccount.id,
          name: 'instagram',
          displayName: `Instagram - @${page.instagramBusinessAccount.username}`,
          connected: true,
          accountId: page.instagramBusinessAccount.id,
          accountName: page.instagramBusinessAccount.username,
          permissions: ['instagram_basic', 'instagram_content_publish'],
          connectedAt: this.lastConnectedAt || new Date(),
          lastSyncAt: this.lastSyncAt
        });
      }
    }

    return platforms;
  }

  async connectPlatform(platform: SocialPlatform['name'], credentials: any): Promise<void> {
    if (platform === 'facebook' || platform === 'instagram') {
      this.credentials = { ...this.credentials, ...credentials };
      await this.connect();
    } else {
      throw new Error(`Platform ${platform} not supported by Facebook/Instagram integration`);
    }
  }

  async disconnectPlatform(platform: SocialPlatform['name']): Promise<void> {
    if (platform === 'facebook' || platform === 'instagram') {
      await this.disconnect();
    } else {
      throw new Error(`Platform ${platform} not supported by Facebook/Instagram integration`);
    }
  }

  async createPost(postData: Omit<SocialPost, 'id' | 'engagement'>): Promise<SocialPost> {
    try {
      let response: any;
      
      if (postData.platform === 'facebook') {
        response = await this.createFacebookPost(postData);
      } else if (postData.platform === 'instagram') {
        response = await this.createInstagramPost(postData);
      } else {
        throw new Error(`Platform ${postData.platform} not supported`);
      }

      const post: SocialPost = {
        id: response.id,
        platform: postData.platform,
        content: postData.content,
        media: postData.media,
        status: 'published',
        publishedTime: new Date(),
        engagement: {
          likes: 0,
          comments: 0,
          shares: 0,
          clicks: 0,
          impressions: 0,
          reach: 0,
          engagementRate: 0,
          lastUpdated: new Date()
        },
        hashtags: postData.hashtags || [],
        mentions: postData.mentions || [],
        campaignId: postData.campaignId
      };

      console.log('Post created:', { post });
      
      return post;
    } catch (error) {
      console.error('Post creation failed:', {
        platform: postData.platform,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async updatePost(postId: string, _updates: Partial<SocialPost>): Promise<SocialPost> {
    try {
      // Facebook and Instagram don't allow editing published posts
      // This would typically be used for updating scheduled posts
      throw new Error('Facebook and Instagram do not support editing published posts');
    } catch (error) {
      console.error('Post update failed:', {
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async deletePost(postId: string): Promise<void> {
    try {
      await this.makeRequest(`/${postId}`, {}, 'DELETE');
      
      console.log('Post deleted:', { postId });
    } catch (error) {
      console.error('Post deletion failed:', {
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getPost(postId: string): Promise<SocialPost | null> {
    try {
      const response = await this.makeRequest(`/${postId}`, {
        fields: 'id,created_time,message,story,permalink_url,insights.metric(post_impressions,post_engaged_users,post_clicks)'
      });

      if (!response.id) return null;

      const engagement = await this.parsePostInsights(response.insights);

      return {
        id: response.id,
        platform: 'facebook', // Would need to determine actual platform
        content: response.message || response.story || '',
        status: 'published',
        publishedTime: new Date(response.created_time),
        engagement,
        hashtags: this.extractHashtags(response.message || ''),
        mentions: this.extractMentions(response.message || '')
      };
    } catch (error) {
      console.error('Failed to get post:', error);
      return null;
    }
  }

  async schedulePost(postData: Omit<SocialPost, 'id' | 'engagement'>, scheduledTime: Date): Promise<SocialPost> {
    try {
      const scheduledTimestamp = Math.floor(scheduledTime.getTime() / 1000);
      
      let response: any;
      
      if (postData.platform === 'facebook') {
        response = await this.scheduleFacebookPost(postData, scheduledTimestamp);
      } else if (postData.platform === 'instagram') {
        // Instagram doesn't support scheduling directly, would need to use Creator Studio API
        throw new Error('Instagram post scheduling requires Facebook Creator Studio API');
      } else {
        throw new Error(`Platform ${postData.platform} not supported`);
      }

      const post: SocialPost = {
        id: response.id,
        platform: postData.platform,
        content: postData.content,
        media: postData.media,
        scheduledTime,
        status: 'scheduled',
        engagement: {
          likes: 0,
          comments: 0,
          shares: 0,
          clicks: 0,
          impressions: 0,
          reach: 0,
          engagementRate: 0,
          lastUpdated: new Date()
        },
        hashtags: postData.hashtags || [],
        mentions: postData.mentions || [],
        campaignId: postData.campaignId
      };

      console.log('Post scheduled:', { post });
      
      return post;
    } catch (error) {
      console.error('Post scheduling failed:', {
        platform: postData.platform,
        scheduledTime,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async publishPost(postId: string): Promise<SocialPost> {
    try {
      // For scheduled posts, this would publish them immediately
      const _response = await this.makeRequest(`/${postId}`, {
        published: true
      }, 'POST');

      const post = await this.getPost(postId);
      if (!post) {
        throw new Error('Failed to retrieve published post');
      }

      post.status = 'published';
      post.publishedTime = new Date();

      console.log('Post published:', { post });
      
      return post;
    } catch (error) {
      console.error('Post publishing failed:', {
        postId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async getEngagementData(postId: string): Promise<SocialEngagement> {
    try {
      const response = await this.makeRequest(`/${postId}/insights`, {
        metric: 'post_impressions,post_engaged_users,post_clicks,post_reactions_like_total,post_comments,post_shares'
      });

      return this.parsePostInsights(response);
    } catch (error) {
      console.error('Failed to get engagement data:', error);
      return {
        likes: 0,
        comments: 0,
        shares: 0,
        clicks: 0,
        impressions: 0,
        reach: 0,
        engagementRate: 0,
        lastUpdated: new Date()
      };
    }
  }

  async createAudience(audienceData: Omit<SocialAudience, 'id'>): Promise<SocialAudience> {
    try {
      const adAccountId = await this.getAdAccountId();
      
      const requestData: any = {
        name: audienceData.name,
        subtype: 'CUSTOM'
      };

      // Handle different audience types
      if (audienceData.criteria.customAudience?.emails) {
        requestData.customer_file_source = 'USER_PROVIDED_ONLY';
        // Would need to hash emails according to Facebook requirements
        const hashedEmails = audienceData.criteria.customAudience.emails.map(email => 
          this.hashEmail(email)
        );
        requestData.payload = {
          schema: ['EMAIL_SHA256'],
          data: hashedEmails.map(hash => [hash])
        };
      }

      const response = await this.makeRequest(`/act_${adAccountId}/customaudiences`, requestData, 'POST');

      const audience: SocialAudience = {
        id: response.id,
        name: audienceData.name,
        platform: audienceData.platform,
        size: 0, // Will be updated once Facebook processes the audience
        criteria: audienceData.criteria,
        lookalike: audienceData.lookalike
      };

      console.log('Audience created:', { audience });
      
      return audience;
    } catch (error) {
      console.error('Audience creation failed:', {
        audienceName: audienceData.name,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async syncAudience(audienceId: string, emails: string[]): Promise<void> {
    try {
      const hashedEmails = emails.map(email => this.hashEmail(email));
      
      await this.makeRequest(`/${audienceId}/users`, {
        payload: {
          schema: ['EMAIL_SHA256'],
          data: hashedEmails.map(hash => [hash])
        }
      }, 'POST');

      console.log('Audience synced:', {
        audienceId,
        emailCount: emails.length
      });
    } catch (error) {
      console.error('Audience sync failed:', {
        audienceId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  async createLookalikeAudience(sourceAudienceId: string, similarity: number): Promise<SocialAudience> {
    try {
      const adAccountId = await this.getAdAccountId();
      
      const response = await this.makeRequest(`/act_${adAccountId}/customaudiences`, {
        name: `Lookalike - ${sourceAudienceId}`,
        subtype: 'LOOKALIKE',
        origin_audience_id: sourceAudienceId,
        lookalike_spec: {
          ratio: similarity / 100, // Convert percentage to ratio
          country: 'US' // Would be configurable
        }
      }, 'POST');

      const audience: SocialAudience = {
        id: response.id,
        name: `Lookalike - ${sourceAudienceId}`,
        platform: 'facebook',
        size: 0, // Will be updated once Facebook processes the audience
        criteria: {},
        lookalike: {
          sourceAudienceId,
          similarity
        }
      };

      console.log('Lookalike audience created:', { audience });
      
      return audience;
    } catch (error) {
      console.error('Lookalike audience creation failed:', {
        sourceAudienceId,
        similarity,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  // Private helper methods

  private async createFacebookPost(postData: Omit<SocialPost, 'id' | 'engagement'>): Promise<any> {
    const pageId = this.getPageIdForPlatform('facebook');
    
    const requestData: any = {
      message: postData.content
    };

    // Handle media attachments
    if (postData.media && postData.media.length > 0) {
      if (postData.media.length === 1) {
        const media = postData.media[0];
        if (media.type === 'image') {
          requestData.url = media.url;
        } else if (media.type === 'video') {
          requestData.source = media.url;
        }
      } else {
        // Multiple media - create as album
        requestData.attached_media = postData.media.map((media, index) => ({
          media_fbid: `media_${index}` // Would need to upload media first
        }));
      }
    }

    return await this.makeRequest(`/${pageId}/feed`, requestData, 'POST');
  }

  private async createInstagramPost(postData: Omit<SocialPost, 'id' | 'engagement'>): Promise<any> {
    const instagramAccountId = this.getInstagramAccountId();
    
    if (!postData.media || postData.media.length === 0) {
      throw new Error('Instagram posts require media');
    }

    const media = postData.media[0];
    
    // Step 1: Create media container
    const containerResponse = await this.makeRequest(`/${instagramAccountId}/media`, {
      image_url: media.url,
      caption: postData.content,
      media_type: media.type.toUpperCase()
    }, 'POST');

    // Step 2: Publish the media
    const publishResponse = await this.makeRequest(`/${instagramAccountId}/media_publish`, {
      creation_id: containerResponse.id
    }, 'POST');

    return publishResponse;
  }

  private async scheduleFacebookPost(postData: Omit<SocialPost, 'id' | 'engagement'>, scheduledTimestamp: number): Promise<any> {
    const pageId = this.getPageIdForPlatform('facebook');
    
    const requestData: any = {
      message: postData.content,
      published: false,
      scheduled_publish_time: scheduledTimestamp
    };

    // Handle media attachments (similar to createFacebookPost)
    if (postData.media && postData.media.length > 0) {
      const media = postData.media[0];
      if (media.type === 'image') {
        requestData.url = media.url;
      } else if (media.type === 'video') {
        requestData.source = media.url;
      }
    }

    return await this.makeRequest(`/${pageId}/feed`, requestData, 'POST');
  }

  private async makeRequest(endpoint: string, params: any = {}, method: 'GET' | 'POST' | 'DELETE' = 'GET'): Promise<any> {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    
    // Add access token to all requests
    params.access_token = this.credentials.accessToken;
    
    let response: Response;
    
    if (method === 'GET' || method === 'DELETE') {
      // Add params to URL for GET/DELETE requests
      Object.keys(params).forEach(key => {
        url.searchParams.append(key, params[key]);
      });
      
      response = await fetch(url.toString(), { method });
    } else {
      // POST request with form data
      const formData = new FormData();
      Object.keys(params).forEach(key => {
        if (typeof params[key] === 'object') {
          formData.append(key, JSON.stringify(params[key]));
        } else {
          formData.append(key, params[key]);
        }
      });
      
      response = await fetch(url.toString(), {
        method: 'POST',
        body: formData
      });
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Facebook API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    return await response.json();
  }

  private parsePostInsights(insights: any): SocialEngagement {
    const engagement: SocialEngagement = {
      likes: 0,
      comments: 0,
      shares: 0,
      clicks: 0,
      impressions: 0,
      reach: 0,
      engagementRate: 0,
      lastUpdated: new Date()
    };

    if (!insights?.data) return engagement;

    for (const insight of insights.data) {
      const value = insight.values?.[0]?.value || 0;
      
      switch (insight.name) {
        case 'post_impressions':
          engagement.impressions = value;
          break;
        case 'post_engaged_users':
          engagement.reach = value;
          break;
        case 'post_clicks':
          engagement.clicks = value;
          break;
        case 'post_reactions_like_total':
          engagement.likes = value;
          break;
        case 'post_comments':
          engagement.comments = value;
          break;
        case 'post_shares':
          engagement.shares = value;
          break;
      }
    }

    // Calculate engagement rate
    if (engagement.reach > 0) {
      const totalEngagements = engagement.likes + engagement.comments + engagement.shares + engagement.clicks;
      engagement.engagementRate = (totalEngagements / engagement.reach) * 100;
    }

    return engagement;
  }

  private extractHashtags(content: string): string[] {
    const hashtagRegex = /#[\w]+/g;
    return content.match(hashtagRegex) || [];
  }

  private extractMentions(content: string): string[] {
    const mentionRegex = /@[\w]+/g;
    return content.match(mentionRegex) || [];
  }

  private getPageIdForPlatform(platform: 'facebook' | 'instagram'): string {
    if (platform === 'facebook') {
      const facebookPage = this.connectedPages.find(page => page.id);
      if (!facebookPage) {
        throw new Error('No Facebook page connected');
      }
      return facebookPage.id;
    }
    
    throw new Error(`Platform ${platform} not supported for page ID lookup`);
  }

  private getInstagramAccountId(): string {
    const pageWithInstagram = this.connectedPages.find(page => page.instagramBusinessAccount);
    if (!pageWithInstagram?.instagramBusinessAccount) {
      throw new Error('No Instagram business account connected');
    }
    return pageWithInstagram.instagramBusinessAccount.id;
  }

  private async getAdAccountId(): Promise<string> {
    // Get the first available ad account
    const response = await this.makeRequest('/me/adaccounts', {
      fields: 'id,name'
    });
    
    if (!response.data || response.data.length === 0) {
      throw new Error('No ad accounts available');
    }
    
    return response.data[0].id.replace('act_', '');
  }

  private hashEmail(email: string): string {
    // In a real implementation, this would use a proper SHA-256 hash
    // For now, returning a placeholder
    return `hashed_${email.toLowerCase().trim()}`;
  }

  /**
   * Get the last sync time for this integration
   */
  getLastSyncTime(): Date | undefined {
    return this.lastSyncAt;
  }

  /**
   * Update the last sync time
   */
  protected updateLastSyncTime(): void {
    this.lastSyncAt = new Date();
  }
}